// src/components/settings/AvatarUploadModal.tsx
//
// Profile-picture picker + preview + upload modal.
//
// Lives on top of `useAvatarUpload` (validation + Storage call) and
// persists the resulting URL directly to `public.profiles.avatar_url`
// via the existing supabase client. The parent's state is informed via
// `onChange(url)` so the live Avatar repaints without a full reload.
//
// UX states surface inline (no toast) so the operator always sees the
// most recent attempt's outcome next to the file they're trying to
// upload.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { supabase } from "../../lib/supabase";
import {
  AVATAR_ALLOWED_EXT_LABEL,
  AVATAR_ALLOWED_MIMES,
  useAvatarUpload,
  validateFile,
} from "../../hooks/useAvatarUpload";

interface AvatarUploadModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  currentAvatarUrl: string | null;
  initials: string;
  /** Called with the new public URL (or null on remove) so the parent's
   * `profile.avatar_url` state stays in sync without a refetch. */
  onChange: (url: string | null) => void;
}

const ACCEPT_ATTR = AVATAR_ALLOWED_MIMES.join(",");

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  open,
  onClose,
  userId,
  currentAvatarUrl,
  initials,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { uploadAvatar, removeAvatar, uploading } = useAvatarUpload();

  const [chosenFile, setChosenFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [validation, setValidation] = useState<
    | { kind: "ok"; sizeMb: string; typeLabel: string }
    | { kind: "error"; reason: string }
    | null
  >(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  // Free preview blob URL when file changes or modal closes.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset state every time the modal closes. previewUrl is read inside
  // a ref-style guard via the cleanup effect above; we intentionally
  // don't depend on it here to avoid re-triggering on every preview swap.
  useEffect(() => {
    if (open) return;
    setChosenFile(null);
    setPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return null;
    });
    setValidation(null);
    setSubmitError(null);
    setRemoving(false);
  }, [open]);

  const handleFilePicked = useCallback(
    async (file: File) => {
      setSubmitError(null);
      const result = await validateFile(file);
      if (!result.ok) {
        setChosenFile(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
        setValidation({ kind: "error", reason: result.reason });
        return;
      }
      // Free prior preview.
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const next = URL.createObjectURL(file);
      setChosenFile(file);
      setPreviewUrl(next);
      setValidation({
        kind: "ok",
        sizeMb: (file.size / (1024 * 1024)).toFixed(2),
        typeLabel: result.type.toUpperCase(),
      });
    },
    [previewUrl]
  );

  const handleBrowseClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void handleFilePicked(file);
    }
    // Allow re-picking the same file after a reset.
    event.target.value = "";
  };

  const handleUpload = async () => {
    if (!chosenFile || !validation || validation.kind !== "ok") {
      return;
    }
    setSubmitError(null);
    const result = await uploadAvatar(chosenFile, userId);
    if (!result.ok) {
      setSubmitError(result.reason);
      return;
    }

    // Best-effort cleanup of the previous avatar from our bucket (no
    // hard fail if the delete bounces — the new URL is already saved).
    if (currentAvatarUrl) {
      void removeAvatar(currentAvatarUrl, userId);
    }

    // Persist to profiles.avatar_url. Trigger validates the URL shape.
    const { error: dbError } = await supabase
      .from("profiles")
      .update({ avatar_url: result.url })
      .eq("id", userId);
    if (dbError) {
      setSubmitError(`Saved photo, but profile update failed: ${dbError.message}`);
      return;
    }

    onChange(result.url);
    onClose();
  };

  const handleRemove = async () => {
    setSubmitError(null);
    setRemoving(true);
    try {
      const removed = await removeAvatar(currentAvatarUrl, userId);
      if (!removed.ok) {
        setSubmitError(removed.reason);
        return;
      }
      const { error: dbError } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", userId);
      if (dbError) {
        setSubmitError(`Photo removed, but profile update failed: ${dbError.message}`);
        return;
      }
      onChange(null);
      onClose();
    } finally {
      setRemoving(false);
    }
  };

  const previewSrc = previewUrl ?? currentAvatarUrl ?? undefined;

  return (
    <Dialog open={open} onClose={uploading || removing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Profile picture
        <IconButton
          onClick={onClose}
          size="small"
          disabled={uploading || removing}
          aria-label="Close avatar upload"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} alignItems="center">
          <Avatar
            src={previewSrc}
            sx={{ width: 120, height: 120, fontSize: 40 }}
          >
            {initials}
          </Avatar>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            {AVATAR_ALLOWED_EXT_LABEL} · Max 5 MB
          </Typography>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            hidden
            onChange={handleInputChange}
            data-testid="avatar-file-input"
          />

          <Button
            variant="outlined"
            onClick={handleBrowseClick}
            disabled={uploading || removing}
          >
            {chosenFile ? "Choose a different photo" : "Browse files"}
          </Button>

          {validation?.kind === "ok" && (
            <Typography variant="caption" color="success.main">
              ✓ {validation.typeLabel} · {validation.sizeMb} MB · bytes verified
            </Typography>
          )}
          {validation?.kind === "error" && (
            <Typography variant="caption" color="error.main">
              ✗ {validation.reason}
            </Typography>
          )}
          {submitError && (
            <Typography variant="caption" color="error.main">
              ✗ {submitError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          color="error"
          onClick={handleRemove}
          disabled={!currentAvatarUrl || uploading || removing}
        >
          {removing ? <CircularProgress size={18} /> : "Remove"}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={uploading || removing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!chosenFile || validation?.kind !== "ok" || uploading || removing}
          >
            {uploading ? <CircularProgress size={18} /> : "Save photo"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarUploadModal;
